import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Badge, Box, Button, HStack, Input, InputGroup, Spinner, Text, VStack } from "@chakra-ui/react";
import {
    HiOutlineBuildingOffice2,
    HiOutlineCog6Tooth,
    HiOutlineDocumentText,
    HiOutlineLockClosed,
    HiOutlineUser,
} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { useAuth } from "@/auth/AuthContext";
import AccountService from "@/services/AccountService";
import { toaster } from "@/components/ui/toaster";
import { Field, FormInput, FormTextarea } from "@/components/jobOffer/ui/inputs";
import PasswordInput from "@/components/ui/foundation/PasswordInput";
import SectionCard from "./SectionCard";
import UsersSection from "./UsersSection";
import TwoFactorSection from "./TwoFactorSection";
import ChatConsentsSection from "./ChatConsentsSection";

const emptyProfile = {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    additionalInfo: "",
    companyName: "",
    companyNip: "",
    companyWebsite: "",
    billingStreet: "",
    billingBuildingNumber: "",
    billingPostalCode: "",
    billingCity: "",
    billingCountry: "",
};

const emptyPasswordForm = { currentPassword: "", newPassword: "", confirmPassword: "" };

const AccountSettingsPage = () => {
    const { t } = useTranslation("account");
    const { updateCurrentUser, isOwner } = useAuth();
    useSetPageHeader(t("settingsPage.title"), t("settingsPage.subtitle"), <HiOutlineCog6Tooth />);

    const profileValidationSchema = Yup.object({
        firstName: Yup.string().trim().required(t("settingsPage.errors.required")),
        lastName: Yup.string().trim().required(t("settingsPage.errors.required")),
        phoneNumber: Yup.string().nullable(),
        additionalInfo: Yup.string().nullable(),
        companyName: Yup.string().nullable(),
        companyNip: Yup.string().nullable()
            .matches(/^\d{10}$/, { message: t("settingsPage.errors.invalidNip"), excludeEmptyString: true }),
        companyWebsite: Yup.string().nullable().url(t("settingsPage.errors.invalidUrl")),
        billingStreet: Yup.string().nullable(),
        billingBuildingNumber: Yup.string().nullable(),
        billingPostalCode: Yup.string().nullable(),
        billingCity: Yup.string().nullable(),
        billingCountry: Yup.string().nullable(),
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [email, setEmail] = useState("");
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [twoFactorEnabledAt, setTwoFactorEnabledAt] = useState(null);
    const [twoFactorMethod, setTwoFactorMethod] = useState(null);

    const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
    const [passwordErrors, setPasswordErrors] = useState({});
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const formik = useFormik({
        initialValues: emptyProfile,
        validationSchema: profileValidationSchema,
        onSubmit: async (values) => {
            setIsSaving(true);
            try {
                const updated = await AccountService.updateProfile(values);
                formik.resetForm({ values });
                setIsEditing(false);
                updateCurrentUser({ firstName: updated.firstName, lastName: updated.lastName });
                toaster.create({ type: "success", title: t("settingsPage.toastSaveSuccess") });
            } catch {
                toaster.create({ type: "error", title: t("settingsPage.toastSaveError") });
            } finally {
                setIsSaving(false);
            }
        },
    });

    const loadTwoFactorState = () => {
        AccountService.getProfile().then((profile) => {
            setTwoFactorEnabled(!!profile.twoFactorEnabled);
            setTwoFactorEnabledAt(profile.twoFactorEnabledAt ?? null);
            setTwoFactorMethod(profile.twoFactorMethod ?? null);
        });
    };

    useEffect(() => {
        AccountService.getProfile()
            .then((profile) => {
                setEmail(profile.email ?? "");
                setTwoFactorEnabled(!!profile.twoFactorEnabled);
                setTwoFactorEnabledAt(profile.twoFactorEnabledAt ?? null);
                setTwoFactorMethod(profile.twoFactorMethod ?? null);
                // resetForm (not setValues) so the loaded data becomes the new baseline
                // that "Anuluj" reverts to - otherwise cancel would revert to the blank
                // emptyProfile the form was initialized with, wiping firstName/lastName.
                formik.resetForm({
                    values: {
                        firstName: profile.firstName ?? "",
                        lastName: profile.lastName ?? "",
                        phoneNumber: profile.phoneNumber ?? "",
                        additionalInfo: profile.additionalInfo ?? "",
                        companyName: profile.companyName ?? "",
                        companyNip: profile.companyNip ?? "",
                        companyWebsite: profile.companyWebsite ?? "",
                        billingStreet: profile.billingStreet ?? "",
                        billingBuildingNumber: profile.billingBuildingNumber ?? "",
                        billingPostalCode: profile.billingPostalCode ?? "",
                        billingCity: profile.billingCity ?? "",
                        billingCountry: profile.billingCountry ?? "",
                    },
                });
            })
            .catch(() => toaster.create({ type: "error", title: t("settingsPage.toastLoadError") }))
            .finally(() => setIsLoading(false));
    }, []);

    const handleCancel = () => {
        formik.resetForm();
        setIsEditing(false);
    };

    const validatePasswordForm = () => {
        const errors = {};
        if (!passwordForm.currentPassword) errors.currentPassword = t("settingsPage.security.errorCurrentRequired");
        if (passwordForm.newPassword.length < 8) errors.newPassword = t("settingsPage.security.errorPasswordLength");
        if (passwordForm.newPassword !== passwordForm.confirmPassword) errors.confirmPassword = t("settingsPage.security.errorPasswordMismatch");
        return errors;
    };

    const handleChangePassword = async () => {
        const errors = validatePasswordForm();
        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }
        setPasswordErrors({});
        setIsChangingPassword(true);
        try {
            await AccountService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
            setPasswordForm(emptyPasswordForm);
            toaster.create({ type: "success", title: t("settingsPage.security.toastSuccess") });
        } catch (err) {
            const message = err?.response?.status === 400
                ? t("settingsPage.security.errorCurrentInvalid")
                : t("settingsPage.security.toastError");
            toaster.create({ type: "error", title: message });
        } finally {
            setIsChangingPassword(false);
        }
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    const disabled = !isEditing;
    // Company & invoice-address data belong to the account Owner only — a User sees them
    // read-only regardless of the page's own edit mode.
    const companyDisabled = disabled || !isOwner;

    return (
        <VStack w="full" py={6} gap={6} align="flex-start" as="form" onSubmit={formik.handleSubmit}>
            <HStack w="full" justify="flex-end">
                {isEditing ? (
                    <HStack gap={2}>
                        <Button variant="outlined" size="sm" onClick={handleCancel} disabled={isSaving} type="button">
                            {t("settingsPage.cancelButton")}
                        </Button>
                        <Button variant="primary" size="sm" type="submit" loading={isSaving}>
                            {t("settingsPage.saveButton")}
                        </Button>
                    </HStack>
                ) : (
                    <Button variant="primary" size="sm" onClick={() => setIsEditing(true)} type="button">
                        {t("settingsPage.editButton")}
                    </Button>
                )}
            </HStack>

            <SectionCard icon={<HiOutlineUser />} title={t("settingsPage.personal.title")} subtitle={t("settingsPage.personal.subtitle")}>
                <HStack w="full" gap={4} align="flex-start" flexWrap="wrap">
                    <Box flex="1" minW="200px">
                        <Field label={t("settingsPage.personal.firstName")} required invalid={formik.touched.firstName && !!formik.errors.firstName}>
                            <FormInput name="firstName" value={formik.values.firstName} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabled} />
                        </Field>
                    </Box>
                    <Box flex="1" minW="200px">
                        <Field label={t("settingsPage.personal.lastName")} required invalid={formik.touched.lastName && !!formik.errors.lastName}>
                            <FormInput name="lastName" value={formik.values.lastName} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabled} />
                        </Field>
                    </Box>
                </HStack>
                <Box mt={4}>
                    <Field label={t("settingsPage.personal.phone")}>
                        <FormInput name="phoneNumber" value={formik.values.phoneNumber} onChange={formik.handleChange} disabled={disabled} />
                    </Field>
                </Box>
                <Box mt={4}>
                    <Field label={t("settingsPage.personal.additionalInfo")} hint={t("settingsPage.personal.additionalInfoHint")}>
                        <FormTextarea
                            name="additionalInfo"
                            value={formik.values.additionalInfo}
                            onChange={formik.handleChange}
                            disabled={disabled}
                            placeholder={t("settingsPage.personal.additionalInfoPlaceholder")}
                        />
                    </Field>
                </Box>
            </SectionCard>

            <SectionCard icon={<HiOutlineBuildingOffice2 />} title={t("settingsPage.company.title")} subtitle={t("settingsPage.company.subtitle")}>
                <Box mb={4}>
                    <Field label={t("settingsPage.company.name")} invalid={formik.touched.companyName && !!formik.errors.companyName}>
                        <FormInput name="companyName" value={formik.values.companyName} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={companyDisabled} />
                    </Field>
                </Box>
                <HStack w="full" gap={4} align="flex-start" flexWrap="wrap">
                    <Box flex="1" minW="200px">
                        <Field
                            label={t("settingsPage.company.nip")}
                            hint={t("settingsPage.company.nipHint")}
                            invalid={formik.touched.companyNip && !!formik.errors.companyNip}
                        >
                            <FormInput name="companyNip" value={formik.values.companyNip} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={companyDisabled} />
                        </Field>
                    </Box>
                    <Box flex="1" minW="200px">
                        <Field label={t("settingsPage.company.website")} invalid={formik.touched.companyWebsite && !!formik.errors.companyWebsite}>
                            <FormInput name="companyWebsite" value={formik.values.companyWebsite} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={companyDisabled} />
                        </Field>
                    </Box>
                </HStack>
            </SectionCard>

            <SectionCard icon={<HiOutlineDocumentText />} title={t("settingsPage.billing.title")} subtitle={t("settingsPage.billing.subtitle")}>
                <HStack w="full" gap={4} align="flex-start" flexWrap="wrap" mb={4}>
                    <Box flex="2" minW="200px">
                        <Field label={t("settingsPage.billing.street")}>
                            <FormInput name="billingStreet" value={formik.values.billingStreet} onChange={formik.handleChange} disabled={companyDisabled} />
                        </Field>
                    </Box>
                    <Box flex="1" minW="150px">
                        <Field label={t("settingsPage.billing.buildingNumber")}>
                            <FormInput name="billingBuildingNumber" value={formik.values.billingBuildingNumber} onChange={formik.handleChange} disabled={companyDisabled} />
                        </Field>
                    </Box>
                </HStack>
                <HStack w="full" gap={4} align="flex-start" flexWrap="wrap">
                    <Box flex="1" minW="150px">
                        <Field label={t("settingsPage.billing.postalCode")}>
                            <FormInput name="billingPostalCode" value={formik.values.billingPostalCode} onChange={formik.handleChange} disabled={companyDisabled} />
                        </Field>
                    </Box>
                    <Box flex="1" minW="150px">
                        <Field label={t("settingsPage.billing.city")}>
                            <FormInput name="billingCity" value={formik.values.billingCity} onChange={formik.handleChange} disabled={companyDisabled} />
                        </Field>
                    </Box>
                    <Box flex="1" minW="150px">
                        <Field label={t("settingsPage.billing.country")}>
                            <FormInput name="billingCountry" value={formik.values.billingCountry} onChange={formik.handleChange} disabled={companyDisabled} />
                        </Field>
                    </Box>
                </HStack>
            </SectionCard>

            <SectionCard icon={<HiOutlineLockClosed />} title={t("settingsPage.security.title")} subtitle={t("settingsPage.security.subtitle")}>
                <Box mb={5}>
                    <Field label={t("settingsPage.security.email")} hint={t("settingsPage.security.emailHint")}>
                        <InputGroup
                            w="full"
                            endElement={
                                <Badge bg="bg.dark" color="text.grey" borderRadius={6} px={2} py={1} fontSize="xs">
                                    {t("settingsPage.security.emailReadOnly")}
                                </Badge>
                            }
                        >
                            <Input value={email} disabled bg="bg.dark" borderColor="border.primary" color="white" pr="24" />
                        </InputGroup>
                    </Field>
                </Box>

                <Text color="text.grey" fontSize="xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={3}>
                    {t("settingsPage.security.changePasswordTitle")}
                </Text>
                <Box mb={4}>
                    <Field label={t("settingsPage.security.currentPassword")} invalid={!!passwordErrors.currentPassword}>
                        <PasswordInput
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))}
                        />
                    </Field>
                    {passwordErrors.currentPassword && <Text color="red.400" fontSize="xs" mt={1}>{passwordErrors.currentPassword}</Text>}
                </Box>
                <HStack w="full" gap={4} align="flex-start" flexWrap="wrap" mb={2}>
                    <Box flex="1" minW="200px">
                        <Field label={t("settingsPage.security.newPassword")} hint={t("settingsPage.security.newPasswordHint")} invalid={!!passwordErrors.newPassword}>
                            <PasswordInput
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
                            />
                        </Field>
                        {passwordErrors.newPassword && <Text color="red.400" fontSize="xs" mt={1}>{passwordErrors.newPassword}</Text>}
                    </Box>
                    <Box flex="1" minW="200px">
                        <Field label={t("settingsPage.security.confirmPassword")} invalid={!!passwordErrors.confirmPassword}>
                            <PasswordInput
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                            />
                        </Field>
                        {passwordErrors.confirmPassword && <Text color="red.400" fontSize="xs" mt={1}>{passwordErrors.confirmPassword}</Text>}
                    </Box>
                </HStack>
                <HStack w="full" justify="flex-end">
                    <Button variant="primary" size="sm" onClick={handleChangePassword} loading={isChangingPassword} type="button">
                        {t("settingsPage.security.changePasswordButton")}
                    </Button>
                </HStack>
            </SectionCard>

            <TwoFactorSection
                enabled={twoFactorEnabled}
                method={twoFactorMethod}
                enabledAt={twoFactorEnabledAt}
                email={email}
                onChanged={loadTwoFactorState}
            />

            <UsersSection />

            <ChatConsentsSection />
        </VStack>
    );
};

export default AccountSettingsPage;
